import React, { useEffect, useRef } from 'react';

import { useIntl } from 'react-intl';

import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import { fg } from '@atlaskit/platform-feature-flags';

import SharedSalesforceDynamicMultiSelect from '../../../../../common/ui/filters/product-filters/shared-salesforce-dynamic-multi-select';
import type { FilterProps } from '../../../../../common/ui/filters/product-filters/types';
import { useSelectFilter } from '../../../../../controllers/filters/base-select-filter';
import {
	useFiltersDiscoverability2Enabled,
	useIsConfigLoaded,
} from '../../../../../controllers/store/bootstrap';
import { filter as caseStatusFilter } from '../../../../../controllers/store/search/filters/case-status';

import { i18n } from './messages';

const CaseStatusFilter = (props: FilterProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const isFiltersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const [filterData, { loadInitialOptions, setOption, clearOptions }] = useSelectFilter({
		filterId: caseStatusFilter.id,
	});
	const isCaseStatusFilterEnabled = fg('rovo-fps-3p-ux-sf-case-status-filter');
	const isRetryOnConfigLoadEnabled = fg('rovo-fps-3p-ux-sf-retry-on-config-load');
	const isConfigLoaded = useIsConfigLoaded();
	// See SalesforceStageFilter for the rationale: when the retry FF is on, hold the
	// "did mount" flip until isConfigLoaded is true so the bootstrap-config race
	// cannot leave the filter permanently empty.
	const hasLoadedInitialOptions = useRef(false);

	useEffect(() => {
		if (!isCaseStatusFilterEnabled || hasLoadedInitialOptions.current) {
			return;
		}
		if (isRetryOnConfigLoadEnabled && !isConfigLoaded) {
			return;
		}
		hasLoadedInitialOptions.current = true;
		loadInitialOptions();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isCaseStatusFilterEnabled, isRetryOnConfigLoadEnabled, isConfigLoaded]);

	if (!filterData || !isCaseStatusFilterEnabled) {
		return null;
	}

	const { isLoading, suggestedOptions, selectedOptions } = filterData;

	return (
		<SharedSalesforceDynamicMultiSelect
			{...props}
			filterId={caseStatusFilter.id}
			label={formatMessage(i18n.caseStatusFilterLabel)}
			noOptionsMessage={formatMessage(i18n.caseStatusFilterNoOptions)}
			iconBefore={<StatusInformationIcon label="" />}
			isLoading={isLoading}
			selectedOptions={selectedOptions}
			suggestedOptions={suggestedOptions}
			isFiltersDiscoverability2Enabled={isFiltersDiscoverability2Enabled}
			loadInitialOptions={loadInitialOptions}
			setOption={(option) => setOption(option)}
			clearOptions={clearOptions}
		/>
	);
};

export default CaseStatusFilter;
